import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import { usePortal } from "hooks/usePortal";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pressable, StyleSheet, Text, View } from "react-native";

const classes = StyleSheet.create({
  labelContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 3,
    marginTop: 5,
    marginLeft: 10,
    marginBottom: 5,
  },
  text: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
  },
  tooltipContainer: {
    color: theme.color.darkGrey,
    backgroundColor: theme.color.white,
    padding: 10,
    position: "absolute",
    marginTop: "-10%",
    right: "1%",
    borderRadius: 5,
    borderTopEndRadius: 0,
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
    elevation: 3,
    zIndex: 1,
  },
  tooltipFullscreen: {
    position: "fixed",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 4,
  },
});

export default function TooltipText(props) {
  const { tooltip, children, tooltipStyle } = props;

  const [isTooltipShown, setIsTooltipShown] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);
  const portalDiv = usePortal();

  useEffect(() => {
    if (!isTooltipShown) {
      return;
    }
    const handleClose = () => setIsTooltipShown(false);

    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);

    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [isTooltipShown]);

  const toggleTooltip = useCallback(() => {
    setIsTooltipShown((currentTooltip) => {
      if (!currentTooltip && iconRef.current) {
        // Only measure when opening
        const rect = iconRef.current.getBoundingClientRect();
        // Position tooltip below and aligned to the left of the icon
        setTooltipPos({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
      return !currentTooltip;
    });
  }, []);

  // Tooltip popup content
  const tooltipPopup = isTooltipShown && (
    <>
      <button
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 1001,
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
        onClick={(event) => {
          event.stopPropagation();
          toggleTooltip();
        }}
        aria-label="Close tooltip"
      />
      <div
        style={{
          position: "fixed",
          zIndex: 1002,
          background: theme.color.white,
          color: theme.color.darkGrey,
          padding: 10,
          borderRadius: 5,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          maxWidth: 320,
          whiteSpace: "normal",
          wordBreak: "break-word",
          left: tooltipPos.left,
          top: tooltipPos.top,
          ...tooltipStyle,
        }}
      >
        {tooltip}
      </div>
    </>
  );

  return (
    <View>
      <View style={classes.labelContainer}>
        <Text style={classes.text}>{children}</Text>
        <Pressable onPress={toggleTooltip} onClick={(event) => event.stopPropagation()}>
          <span ref={iconRef} style={{ display: "inline-flex" }}>
            <Icon name="alert-circle" size={15} color={theme.color.black} />
          </span>
        </Pressable>
      </View>
      {portalDiv && createPortal(tooltipPopup, portalDiv)}
    </View>
  );
}
